import { UserType } from '../interfaces/userType.enum';

/** Everything landingPathFor needs; RoleLanding builds it from the signed-in user. */
export interface LandingContext {
    /** The JWT roles, already split into tokens. */
    roles: UserType[];
    /** True when the user heads a department (JWT `isDepartmentAdmin` claim). */
    isDepartmentAdmin?: boolean;
}

/** The dashboard route: `Dashboard` renders the board or personal dashboard for the caller's role. */
export const HOME_PATH = '/';

/** SaaS Operators have no institution of their own, so '/' has nothing for them; land on their console. */
export const OPERATOR_HOME = '/saas/premium-provisioning';

interface Rule {
    /** Does this rule apply to the user? */
    matches: (c: LandingContext) => boolean;
    path: string;
}

/**
 * Ordered rules, first match wins; anyone left over lands on '/'. Every tenant role lands on '/':
 * `Dashboard` already picks the right board there (owner/manager/accountant board, or the personal
 * dashboard with "My team — today" for a department head), and the module boards are one click away
 * in the sidebar. The rules exist for users '/' does not serve.
 */
const RULES: Rule[] = [
    { matches: c => c.roles.includes(UserType.SystemAdmin), path: HOME_PATH },
    { matches: c => c.roles.includes(UserType.SaaSOperator), path: OPERATOR_HOME },
];

export function landingPathFor(ctx: LandingContext): string {
    for (const rule of RULES) {
        if (rule.matches(ctx)) return rule.path;
    }
    return HOME_PATH;
}

/** Post-login entry route: RoleLanding resolves it to landingPathFor(...). */
export const LANDING_ROUTE = '/home';

/**
 * Paths that are not deep links: the login page would loop, and a logged-out visit to '/' (or /home)
 * just means "my dashboard", which the role landing resolves.
 */
const NON_TARGETS = ['/login', LANDING_ROUTE, HOME_PATH];

/**
 * Sanitizes a post-login "return to" value (ProtectedRoute's `state.from` string, a PermissionRoute
 * `state.from` Location object, or a `?from=` query value). Only a same-origin, app-relative path
 * survives: it must start with a single '/', not '//' or '/\' (protocol-relative, another origin),
 * contain no control characters or backslashes, and not be the login page itself. Anything else
 * returns null.
 */
export function safeReturnPath(raw: unknown): string | null {
    let path: string | null = null;
    if (typeof raw === 'string') {
        path = raw;
    } else if (raw && typeof raw === 'object' && typeof (raw as { pathname?: unknown }).pathname === 'string') {
        const loc = raw as { pathname: string; search?: unknown; hash?: unknown };
        path = loc.pathname
            + (typeof loc.search === 'string' ? loc.search : '')
            + (typeof loc.hash === 'string' ? loc.hash : '');
    }
    if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
    // eslint-disable-next-line no-control-regex
    if (/[\\\u0000-\u001f\u007f]/.test(path)) return null;
    const pathname = path.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
    if (NON_TARGETS.includes(pathname.toLowerCase())) return null;
    return path;
}

/** The post-login destination: a safe deep link the user was bounced from, else the role landing. */
export function postLoginPath(state: unknown, search: string): string {
    return safeReturnPath((state as { from?: unknown } | null)?.from)
        ?? safeReturnPath(new URLSearchParams(search).get('from'))
        ?? LANDING_ROUTE;
}
